import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import { fetchCourses, submitEnrollment } from "../lib/api";

const STEPS = ["Corso", "Anagrafica", "Genitore / Contatti", "Conferma"];

export default function Iscrizione() {
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    corso: searchParams.get("corso") || "",
    livello: "",
    nome: "",
    cognome: "",
    data_nascita: "",
    email: "",
    telefono: "",
    genitore_nome: "",
    genitore_telefono: "",
    note: "",
  });

  useEffect(() => {
    fetchCourses().then(setCourses).catch(() => {});
  }, []);

  const selectedCourse = courses.find((c) => c.id === form.corso);
  const isMinor = form.data_nascita && (new Date().getFullYear() - new Date(form.data_nascita).getFullYear()) < 18;

  const canNext = () => {
    if (step === 0) return form.corso && form.livello;
    if (step === 1) return form.nome && form.cognome && form.email && form.telefono && form.data_nascita;
    if (step === 2) return !isMinor || (form.genitore_nome && form.genitore_telefono);
    return true;
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const res = await submitEnrollment(form);
      setDone(res);
      toast.success("Richiesta di iscrizione inviata!");
    } catch (err) {
      toast.error("Errore durante l'invio. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="page-iscrizione">
        <PageHero eyebrow="Richiesta ricevuta" title="Grazie!" italic="Ti contatteremo a breve." />
        <section className="py-24 px-6 max-w-2xl mx-auto text-center" data-testid="enrollment-success">
          <CheckCircle2 size={56} className="text-[#A3133A] mx-auto mb-6" />
          <h2 className="font-serif-display text-3xl mb-4">Iscrizione #{done.id.slice(0, 8).toUpperCase()}</h2>
          <p className="text-[#5C5C5C] mb-8">
            La tua richiesta di iscrizione per <strong>{selectedCourse?.nome}</strong> è stata
            registrata. La segreteria di Amici per la Musica APS ti contatterà
            entro 48h per confermare la lezione di prova e il calendario.
          </p>
          <Link to="/" className="bg-[#A3133A] text-white px-6 py-3 text-[12px] tracking-eyebrow rounded-sm inline-block">
            Torna alla Home
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="page-iscrizione">
      <PageHero
        eyebrow="Iscrizione online A.A. 2025/2026"
        title="Un nuovo inizio,"
        italic="in quattro passi."
        subtitle="Compila la richiesta di iscrizione. La segreteria ti ricontatterà per fissare la lezione di prova gratuita."
      />

      <section className="py-16 px-6 md:px-12 lg:px-24 max-w-3xl mx-auto">
        {/* Stepper */}
        <div className="flex items-center justify-between mb-12" data-testid="enrollment-stepper">
          {STEPS.map((label, i) => (
            <div key={label} className="flex-1 flex items-center">
              <div className={`flex flex-col items-center flex-1 ${i <= step ? "text-[#A3133A]" : "text-[#5C5C5C]/40"}`}>
                <div className={`w-9 h-9 border-2 rounded-full flex items-center justify-center text-sm font-medium ${i <= step ? "border-[#A3133A] bg-[#A3133A] text-white" : "border-[#5C5C5C]/30 bg-transparent"}`}>
                  {i + 1}
                </div>
                <div className="text-[10px] tracking-eyebrow mt-2 text-center hidden sm:block">{label}</div>
              </div>
              {i < STEPS.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-[#A3133A]" : "bg-[#5C5C5C]/20"}`} />}
            </div>
          ))}
        </div>

        <div className="border border-[#A3133A]/15 bg-white p-8 sm:p-12">
          {step === 0 && (
            <div data-testid="step-course" className="space-y-6">
              <h2 className="font-serif-display text-3xl text-[#1C1C1C] mb-2">Scegli il corso</h2>
              <div>
                <Label>Corso</Label>
                <select
                  value={form.corso}
                  onChange={(e) => update("corso", e.target.value)}
                  data-testid="enrollment-corso"
                  className="w-full border border-[#A3133A]/30 rounded-sm px-4 py-3 bg-white focus:outline-none focus:border-[#A3133A]"
                >
                  <option value="">— Seleziona un corso —</option>
                  {courses.map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>
              {selectedCourse && (
                <div>
                  <Label>Livello</Label>
                  <select
                    value={form.livello}
                    onChange={(e) => update("livello", e.target.value)}
                    data-testid="enrollment-livello"
                    className="w-full border border-[#A3133A]/30 rounded-sm px-4 py-3 bg-white focus:outline-none focus:border-[#A3133A]"
                  >
                    <option value="">— Seleziona livello —</option>
                    {selectedCourse.livelli.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-personal" className="space-y-5">
              <h2 className="font-serif-display text-3xl text-[#1C1C1C] mb-2">Dati dell'allievo</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Nome" value={form.nome} onChange={(v) => update("nome", v)} testid="field-nome" />
                <Field label="Cognome" value={form.cognome} onChange={(v) => update("cognome", v)} testid="field-cognome" />
              </div>
              <Field label="Data di nascita" type="date" value={form.data_nascita} onChange={(v) => update("data_nascita", v)} testid="field-data-nascita" />
              <Field label="Email" type="email" value={form.email} onChange={(v) => update("email", v)} testid="field-email" />
              <Field label="Telefono" type="tel" value={form.telefono} onChange={(v) => update("telefono", v)} testid="field-telefono" />
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-parent" className="space-y-5">
              <h2 className="font-serif-display text-3xl text-[#1C1C1C] mb-2">
                {isMinor ? "Dati del genitore" : "Note e contatti aggiuntivi"}
              </h2>
              {isMinor ? (
                <>
                  <p className="text-sm text-[#5C5C5C] mb-4">Essendo l'allievo minorenne, inserisci i dati del genitore referente.</p>
                  <Field label="Nome e cognome genitore" value={form.genitore_nome} onChange={(v) => update("genitore_nome", v)} testid="field-genitore-nome" />
                  <Field label="Telefono genitore" type="tel" value={form.genitore_telefono} onChange={(v) => update("genitore_telefono", v)} testid="field-genitore-tel" />
                </>
              ) : (
                <p className="text-sm text-[#5C5C5C]">Allievo maggiorenne — questo passo è opzionale.</p>
              )}
              <div>
                <Label>Note (opzionale)</Label>
                <textarea
                  value={form.note}
                  onChange={(e) => update("note", e.target.value)}
                  data-testid="field-note"
                  rows={4}
                  placeholder="Esperienza pregressa, preferenze di orario, esami Trinity da sostenere..."
                  className="w-full border border-[#A3133A]/30 rounded-sm px-4 py-3 bg-white focus:outline-none focus:border-[#A3133A]"
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-confirm" className="space-y-4">
              <h2 className="font-serif-display text-3xl text-[#1C1C1C] mb-4">Conferma la richiesta</h2>
              <div className="border border-[#A3133A]/15 divide-y divide-[#A3133A]/10 text-sm">
                <Row label="Corso" value={selectedCourse?.nome} />
                <Row label="Livello" value={form.livello} />
                <Row label="Allievo" value={`${form.nome} ${form.cognome}`} />
                <Row label="Email" value={form.email} />
                <Row label="Telefono" value={form.telefono} />
                {isMinor && <Row label="Genitore" value={form.genitore_nome} />}
                {form.note && <Row label="Note" value={form.note} />}
              </div>
              <p className="text-xs text-[#5C5C5C] pt-4">
                Inviando la richiesta accetti di essere ricontattato dalla segreteria
                di Amici per la Musica APS per la gestione dell'iscrizione, ai sensi
                del GDPR 2016/679.
              </p>
            </div>
          )}

          <div className="flex justify-between pt-10 mt-8 border-t border-[#A3133A]/15">
            <button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="enrollment-prev"
              className="text-[12px] tracking-eyebrow text-[#A3133A] inline-flex items-center gap-2 disabled:opacity-30"
            >
              <ArrowLeft size={14} /> Indietro
            </button>
            {step < STEPS.length - 1 ? (
              <button
                onClick={() => canNext() && setStep((s) => s + 1)}
                disabled={!canNext()}
                data-testid="enrollment-next"
                className="bg-[#A3133A] text-white px-6 py-3 text-[12px] tracking-eyebrow rounded-sm inline-flex items-center gap-2 disabled:opacity-40"
              >
                Continua <ArrowRight size={14} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                data-testid="enrollment-submit"
                className="bg-[#A3133A] text-white px-8 py-3 text-[12px] tracking-eyebrow rounded-sm inline-flex items-center gap-2 disabled:opacity-40"
              >
                {loading ? "Invio..." : "Invia richiesta"} <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const Label = ({ children }) => (
  <label className="block text-[11px] tracking-eyebrow text-[#5C5C5C] mb-2">{children}</label>
);

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <Label>{label}</Label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full border border-[#A3133A]/30 rounded-sm px-4 py-3 bg-white focus:outline-none focus:border-[#A3133A]"
    />
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between py-3 px-4 gap-4">
    <span className="text-[11px] tracking-eyebrow text-[#5C5C5C] shrink-0">{label}</span>
    <span className="text-[#1C1C1C] text-right">{value || "—"}</span>
  </div>
);
